/**
 * The sales and reports API, as the screens see it (P3-TR5).
 *
 * Money stays a string on the way in and out (MONEY-2). Nothing here parses
 * one into a number.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { api } from '../../api/client.js';

export interface Customer {
  id: number;
  name: string;
  phone: string | null;
  address: string | null;
  type: 'regular' | 'walkin';
  defaultRate: string | null;
  isActive: boolean;
}

export interface Invoice {
  id: number;
  customerId: number;
  customerName: string;
  month: string;
  total: string;
  paid: string;
  outstanding: string;
  status: 'due' | 'partial' | 'paid';
  sentAt: string | null;
  litres: string;
  ageDays: number;
}

export interface DeliveryGrid {
  customers: { id: number; name: string; defaultRate: string | null }[];
  days: string[];
  cells: Record<string, string>;
}

export interface AgingReport {
  asOf: string;
  customers: {
    customerId: number;
    customerName: string;
    phone: string | null;
    current: string;
    days31to60: string;
    over60: string;
    total: string;
    oldestDays: number;
  }[];
  totals: { current: string; days31to60: string; over60: string; total: string };
}

export interface Reconciliation {
  id: number;
  accountId: number;
  accountName: string;
  asOfDate: string;
  bookBalance: string;
  actualBalance: string;
  difference: string;
  isClean: boolean;
  note: string | null;
}

export interface PnlReport {
  basis: 'cash' | 'accrual';
  from: string;
  to: string;
  income: string;
  expenses: string;
  netProfit: string;
  incomeByCategory: { categoryId: number | null; name: string; amount: string }[];
  expensesByCategory: { categoryId: number | null; name: string; amount: string }[];
  basisNote: string;
}

export interface BreakEven {
  target: string;
  recovered: string;
  remaining: string;
  percent: string;
  isRecovered: boolean;
}

interface Envelope<T> {
  success: true;
  data: T;
}

export const salesKeys = {
  customers: () => ['customers'] as const,
  invoices: () => ['invoices'] as const,
  grid: (from: string, to: string) => ['milk', 'grid', from, to] as const,
  aging: () => ['receivables', 'aging'] as const,
  reconciliations: () => ['reconciliations'] as const,
  pnl: (from: string, to: string, basis: string) => ['reports', 'pnl', from, to, basis] as const,
  breakEven: () => ['reports', 'break-even'] as const,
};

export function useCustomers(): UseQueryResult<Customer[]> {
  return useQuery({
    queryKey: salesKeys.customers(),
    queryFn: async () => (await api.get<Envelope<Customer[]>>('/customers')).data.data,
  });
}

export function useInvoices(filters: { customerId?: number; status?: string } = {}): UseQueryResult<Invoice[]> {
  return useQuery({
    queryKey: [...salesKeys.invoices(), filters],
    queryFn: async () => (await api.get<Envelope<Invoice[]>>('/invoices', { params: filters })).data.data,
    // Only fetched when a customer row is actually opened: the ageing report
    // is read far more often than any one customer is drilled into.
    enabled: filters.customerId === undefined || filters.customerId > 0,
  });
}

export interface NewCustomer {
  name: string;
  type: 'regular' | 'walkin';
  phone?: string;
  address?: string;
  /** Required for a regular customer — it is the rate their monthly bill is
   *  built from. The server says so by name if it is missing. */
  defaultRate?: string;
}

export interface CustomerChanges {
  name?: string;
  type?: 'regular' | 'walkin';
  phone?: string;
  address?: string;
  defaultRate?: string;
  /** BR-08 — deactivation, never deletion. */
  isActive?: boolean;
}

function useCustomerInvalidation(): () => Promise<void> {
  const queryClient = useQueryClient();

  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: salesKeys.customers() }),
      // The round is drawn from the customer list, and the ageing report is
      // grouped by it.
      queryClient.invalidateQueries({ queryKey: ['milk'] }),
      queryClient.invalidateQueries({ queryKey: salesKeys.aging() }),
    ]);
  };
}

export function useCreateCustomer(): UseMutationResult<Customer, unknown, NewCustomer> {
  const invalidate = useCustomerInvalidation();

  return useMutation({
    mutationFn: async (input: NewCustomer) => (await api.post<Envelope<Customer>>('/customers', input)).data.data,
    onSuccess: invalidate,
  });
}

export function useUpdateCustomer(): UseMutationResult<Customer, unknown, { id: number; changes: CustomerChanges }> {
  const invalidate = useCustomerInvalidation();

  return useMutation({
    mutationFn: async ({ id, changes }) =>
      (await api.patch<Envelope<Customer>>(`/customers/${String(id)}`, changes)).data.data,
    onSuccess: invalidate,
  });
}

export interface PaymentInput {
  invoiceId: number;
  date: string;
  /** What the customer handed over (MONEY-2). */
  amount: string;
  /** Where it landed. Required — a payment naming no account is money that
   *  never reaches the books (P1-BR4). */
  accountId: number;
  note?: string;
}

/**
 * Records a payment against a milk bill (BR-24).
 *
 * The endpoint has existed since P3 and nothing called it, so the receivables
 * page could say who owed what and never that they had paid. It posts a
 * `receivable_collection` entry as *pending*, so collecting money goes through
 * maker-checker like everything else (BR-07).
 *
 * Overpayment is refused by the server rather than absorbed: paying two months
 * at once against one bill would hide the second, which would still show as
 * due.
 */
export function useRecordPayment(): UseMutationResult<unknown, unknown, PaymentInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ invoiceId, ...body }: PaymentInput) =>
      (await api.post(`/invoices/${String(invoiceId)}/payments`, body)).data,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: salesKeys.invoices() }),
        queryClient.invalidateQueries({ queryKey: salesKeys.aging() }),
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
        queryClient.invalidateQueries({ queryKey: ['accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'finance'] }),
      ]);
    },
  });
}

export function useDeliveryGrid(from: string, to: string): UseQueryResult<DeliveryGrid> {
  return useQuery({
    queryKey: salesKeys.grid(from, to),
    queryFn: async () =>
      (await api.get<Envelope<DeliveryGrid>>('/milk/deliveries', { params: { from, to } })).data.data,
  });
}

export function useAging(): UseQueryResult<AgingReport> {
  return useQuery({
    queryKey: salesKeys.aging(),
    queryFn: async () => (await api.get<Envelope<AgingReport>>('/receivables/aging')).data.data,
  });
}

export function useReconciliations(): UseQueryResult<Reconciliation[]> {
  return useQuery({
    queryKey: salesKeys.reconciliations(),
    queryFn: async () => (await api.get<Envelope<Reconciliation[]>>('/reconciliations')).data.data,
  });
}

export function usePnl(from: string, to: string, basis: 'cash' | 'accrual'): UseQueryResult<PnlReport> {
  return useQuery({
    queryKey: salesKeys.pnl(from, to, basis),
    queryFn: async () =>
      (await api.get<Envelope<PnlReport>>('/reports/pnl', { params: { from, to, basis } })).data.data,
  });
}

export function useBreakEven(): UseQueryResult<BreakEven> {
  return useQuery({
    queryKey: salesKeys.breakEven(),
    queryFn: async () => (await api.get<Envelope<BreakEven>>('/reports/break-even')).data.data,
  });
}

export interface DeliveryInput {
  date: string;
  lines: { customerId: number; litres: string }[];
}

export function useSaveDeliveries(): UseMutationResult<unknown, unknown, DeliveryInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: DeliveryInput) => (await api.post('/milk/deliveries', input)).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['milk'] });
    },
  });
}

export interface MilkDay {
  date: string;
  collected: string;
  delivered: string;
  soldAtGate: string;
  accountedFor: string;
  /** collected − accountedFor. Positive means litres left and arrived nowhere. */
  difference: string;
  isClean: boolean;
}

export interface MilkBalance {
  from: string;
  to: string;
  days: MilkDay[];
  totals: { collected: string; delivered: string; soldAtGate: string; difference: string };
}

/**
 * Where the day's milk went (BR-23).
 *
 * All milk is sold on this farm, so anything collected and not accounted for is
 * a discrepancy rather than an unrecorded use — which is why the screen names
 * the shortfall instead of tucking it into a category.
 */
export function useMilkBalance(from: string, to: string): UseQueryResult<MilkBalance> {
  return useQuery({
    queryKey: ['milk', 'balance', from, to],
    queryFn: async () => (await api.get<Envelope<MilkBalance>>('/milk/balance', { params: { from, to } })).data.data,
  });
}

export interface WalkinSaleInput {
  date: string;
  /** Litres as a string — the API takes a quantity string, never a float. */
  litres: string;
  /** Price per litre (MONEY-2). */
  rate: string;
  /** Where the money landed. Required: a gate sale is cash in hand. */
  accountId: number;
  customerId?: number;
  note?: string;
}

export interface WalkinSaleResult {
  txnId: number;
  amount: string;
  litres: string;
  rate: string;
}

/**
 * Sells milk at the gate (BR-23).
 *
 * The endpoint has existed since P3 and nothing ever called it, which is why
 * milk could be collected and delivered in the app but never sold. It posts a
 * *pending* income entry, so a milk sale goes through maker-checker like every
 * other figure that reaches a balance (BR-07).
 *
 * Regular customers are refused by the server: their milk is billed monthly by
 * invoice, and selling it at the gate as well would bill them twice.
 */
export function useWalkinSale(): UseMutationResult<WalkinSaleResult, unknown, WalkinSaleInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: WalkinSaleInput) =>
      (await api.post<Envelope<WalkinSaleResult>>('/milk/walkin-sale', input)).data.data,
    onSuccess: async () => {
      // The sale is income against an account, so the approval queue, the
      // balances and the dashboard all move with it.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['transactions'] }),
        queryClient.invalidateQueries({ queryKey: ['accounts'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'finance'] }),
      ]);
    },
  });
}

export interface ReconcileInput {
  accountId: number;
  asOfDate: string;
  actualBalance: string;
  note?: string;
}

export function useReconcile(): UseMutationResult<Reconciliation, unknown, ReconcileInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: ReconcileInput) =>
      (await api.post<Envelope<Reconciliation>>('/reconciliations', input)).data.data,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: salesKeys.reconciliations() }),
        queryClient.invalidateQueries({ queryKey: ['reminders'] }),
      ]);
    },
  });
}

/** The book balance the server computed, before the partner types theirs. */
export function useReconcilePreview(
  accountId: number | null,
  asOf: string,
): UseQueryResult<{ accountName: string; bookBalance: string; lastReconciled: string | null }> {
  return useQuery({
    queryKey: ['reconciliations', 'preview', accountId, asOf],
    queryFn: async () =>
      (
        await api.get<Envelope<{ accountName: string; bookBalance: string; lastReconciled: string | null }>>(
          '/reconciliations/preview',
          { params: { accountId, asOf } },
        )
      ).data.data,
    enabled: accountId !== null,
  });
}
